import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, MapPinIcon, MenuIcon, XIcon } from 'lucide-react';
import { navigation } from '../../data/navigation';
import { images } from '../../data/images';
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <header className={`sticky top-0 z-50 w-full transition-[background-color,box-shadow] duration-200 ease-out ${scrolled ? 'bg-cream/90 shadow-[0_1px_0_rgb(var(--ink)/0.08)] backdrop-blur-md' : 'bg-cream'}`} data-id="element-157" data-mp="3p11o1jswuchu">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-8" data-id="element-158" data-mp="1xddnl8u4qm4">
        <Link to="/" aria-label="Aloesol, retour à l’accueil" className="shrink-0" data-id="element-159" data-mp="19943g7gbqowi">
          <img src={images.logo} alt="Aloesol" className="h-9 w-auto md:h-10" data-id="element-160" data-mp="1daze42k5aemy" />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:block" data-id="element-161" data-mp="3abvdyd5y1fn9">
          <ul className="flex items-center gap-1" data-id="element-162" data-mp="35dafai6cb40x">
            {navigation.map(item => <li key={item.label} className="relative" onMouseEnter={() => item.children && setOpenMenu(item.label)} onMouseLeave={() => item.children && setOpenMenu(null)} data-id="element-163" data-mp="3jd31q1au2y33">
                {item.children ? <button type="button" aria-expanded={openMenu === item.label} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)} className="flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-2 text-[15px] font-medium text-ink/80 transition-colors duration-150 ease-out hover:bg-sand hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aloe-700" data-id="element-164" data-mp="2qm1hhlsaaccp">
                    {item.label}
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${openMenu === item.label ? 'rotate-180' : ''}`} data-id="element-165" data-mp="ieo83wy39l4w" />
                  </button> : <NavLink to={item.href} className={({
              isActive
            }) => `block whitespace-nowrap rounded-full px-4 py-2 text-[15px] font-medium transition-colors duration-150 ease-out hover:bg-sand hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aloe-700 ${isActive ? 'text-aloe-900' : 'text-ink/80'}`} data-id="element-166" data-mp="eenx6ma77s2l">
                    {item.label}
                  </NavLink>}

                <AnimatePresence data-id="element-167" data-mp="3cz5vu9xtrg7o">
                  {item.children && openMenu === item.label && <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.18,
                ease
              }} className="absolute left-0 top-full w-[340px] origin-top-left pt-3" data-id="element-168" data-mp="3htjkzulw8r3w">
                      <ul className="rounded-2xl bg-white p-2 shadow-[0_20px_50px_-20px_rgb(var(--aloe-900)/0.35)] ring-1 ring-ink/5" data-id="element-169" data-mp="3jcbgytfho0xx">
                        {item.children.map(child => <li key={child.href} data-id="element-170" data-mp="fvbh2fvqqwl9">
                            <Link to={child.href} className="block rounded-xl px-4 py-3 transition-colors duration-150 ease-out hover:bg-cream focus-visible:bg-cream focus-visible:outline-none" data-id="element-171" data-mp="axjk37g8oka7">
                              <span className="block text-[15px] font-semibold text-aloe-900" data-id="element-172" data-mp="2qoxphauk7xl7">{child.label}</span>
                              <span className="mt-0.5 block text-sm text-ink/60" data-id="element-173" data-mp="2gmldizezbfho">{child.description}</span>
                            </Link>
                          </li>)}
                      </ul>
                    </motion.div>}
                </AnimatePresence>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-2" data-id="element-174" data-mp="2zc1okgoaiqcc">
          <Link to="/points-de-vente" className="hidden items-center gap-2 whitespace-nowrap rounded-full bg-aloe-900 px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-150 ease-out hover:bg-aloe-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aloe-700 focus-visible:ring-offset-2 focus-visible:ring-offset-cream md:flex" data-id="element-175" data-mp="2zbi8l5i7ac0y">
            <MapPinIcon className="h-4 w-4" data-id="element-176" data-mp="1exrerhyj7q4y" />
            Trouver une pharmacie
          </Link>
          <button type="button" onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu" className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors duration-150 ease-out hover:bg-sand lg:hidden" data-id="element-177" data-mp="1jvt7kulrf3tm">
            <MenuIcon className="h-6 w-6" data-id="element-178" data-mp="1lucft9gnrihz" />
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-179" data-mp="1gsn5jw19f8uy">
        {mobileOpen && <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-180" data-mp="1utfzaejqlzx0">
            <button type="button" aria-label="Fermer le menu" className="absolute inset-0 bg-aloe-900/40" onClick={() => setMobileOpen(false)} data-id="element-181" data-mp="24i7myahrly24" />
            <motion.div role="dialog" aria-modal="true" aria-label="Menu" initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.28,
          ease
        }} className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col bg-cream" data-id="element-182" data-mp="2dkgvwvxdxjde">
              <div className="flex h-20 items-center justify-between px-5" data-id="element-183" data-mp="2j4wmgcc0ob8i">
                <img src={images.logo} alt="Aloesol" className="h-9 w-auto" data-id="element-184" data-mp="2o4j89a5wuzsx" />
                <button type="button" onClick={() => setMobileOpen(false)} aria-label="Fermer le menu" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-sand" data-id="element-185" data-mp="29g4wuaufi30r">
                  <XIcon className="h-6 w-6" data-id="element-186" data-mp="20ddia269ar4w" />
                </button>
              </div>
              <nav aria-label="Navigation mobile" className="flex-1 overflow-y-auto px-5 pb-8" data-id="element-187" data-mp="24hbikg7bi7zj">
                <ul className="divide-y divide-ink/10 border-y border-ink/10" data-id="element-188" data-mp="1qhsk6f4b6b0f">
                  {navigation.map(item => <li key={item.label} data-id="element-189" data-mp="1vi81fhrpo2no">
                      {item.children ? <>
                          <button type="button" aria-expanded={mobileExpanded === item.label} onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)} className="flex w-full items-center justify-between py-4 font-serif text-3xl text-aloe-900" data-id="element-190" data-mp="25zjwlyb9itfj">
                            {item.label}
                            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-out ${mobileExpanded === item.label ? 'rotate-180' : ''}`} data-id="element-191" data-mp="1n795t2tvulxs" />
                          </button>
                          <AnimatePresence initial={false} data-id="element-192" data-mp="19avr21gqgyn0">
                            {mobileExpanded === item.label && <motion.ul initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.22,
                      ease
                    }} className="overflow-hidden" data-id="element-193" data-mp="3fxjv6id6gfv9">
                                {item.children.map(child => <li key={child.href} data-id="element-194" data-mp="2x4z9bkvwbb0y">
                                    <Link to={child.href} className="block pb-3 pl-1 text-base font-medium text-ink/70" data-id="element-195" data-mp="326ol52qxzsq3">
                                      {child.label}
                                    </Link>
                                  </li>)}
                                <li className="h-2" aria-hidden="true" data-id="element-196" data-mp="2ef9fp13vcvam" />
                              </motion.ul>}
                          </AnimatePresence>
                        </> : <Link to={item.href} className="block py-4 font-serif text-3xl text-aloe-900" data-id="element-197" data-mp="20yu8wrfr527p">
                          {item.label}
                        </Link>}
                    </li>)}
                </ul>
                <Link to="/points-de-vente" className="mt-8 flex items-center justify-center gap-2 rounded-full bg-aloe-900 px-5 py-3.5 text-sm font-semibold text-cream" data-id="element-198" data-mp="2evg4tww24gzh">
                  <MapPinIcon className="h-4 w-4" data-id="element-199" data-mp="1m83fxg2fbx30" />
                  Trouver une pharmacie
                </Link>
              </nav>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}